import { useMutation, useQuery } from "@tanstack/react-query"
import { Card, Divider } from "antd"
import type React from "react"
import { useEffect, useReducer, useState } from "react"
import AdminTable from "./components/AdminTable"
import AdminModal from "./components/AdminModal"
import AdminTableActions from "./components/AdminTableActions"
import AdminSearchForm from "./components/AdminSearchForm"
import { adminService, type AdminFormParams, type AdminSearchParams } from "@/services/admin/adminApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

interface PageState {
  modalVisible: boolean
  modalTitle: string
  currentRecord: Partial<any> | null
  selectedRowKeys: React.Key[]
  selectedRows: any[]
}

export const AdminPage: React.FC = () => {
  // const { modal, message } = App.useApp();

  const [state, dispatch] = useReducer(
    (prev: PageState, action: Partial<PageState>) => ({
      ...prev,
      ...action,
    }),
    {
      modalVisible: false,
      modalTitle: "Thêm Admin",
      currentRecord: null,
      selectedRowKeys: [],
      selectedRows: [],
    }
  )

  const [searchParams, setSearchParams] = useTableParams<AdminSearchParams>({
    ...PAGE_CONFIG,
    query: "",
    is_active: undefined,
    role_id: undefined,
  })
  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["admin_list", searchParams],
    queryFn: () => {
      return adminService.list({
        ...searchParams,
      })
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const saveAdminMutation = useMutation({
    mutationFn: (data: AdminFormParams) => {
      if (data.id) {
        return adminService.update(data)
      }
      return adminService.create(data)
    },
    onSuccess: () => {
      dispatch({
        modalVisible: false,
        currentRecord: null,
      })
      refetch()
    },
  })

  /**
   * Xử lý tìm kiếm
   */
  const handleSearch = (values: Omit<AdminSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  /**
   * Xử lý thêm mới tài khoản Admin
   */
  const handleAdd = () => {
    dispatch({
      modalVisible: true,
      modalTitle: "Thêm tài khoản Admin",
      currentRecord: null,
    })
  }

  /**
   * Xử lý chỉnh sửa tài khoản Admin
   */
  const handleEdit = (record) => {
    dispatch({
      modalVisible: true,
      modalTitle: "Cập nhật tài khoản Admin",
      currentRecord: record,
    })
  }

  const handleStatusChange = (record, checked: boolean) => {
    saveAdminMutation.mutate({
      ...record,
      role: record.admin_roles?.map((item: any) => item.role_id),
      is_active: checked ? 1 : 0,
    })
  }

  const handleSelectionChange = (selectedRowKeys: React.Key[], selectedRows: any[]) => {
    dispatch({
      selectedRowKeys,
      selectedRows,
    })
  }

  /**
   * Xử lý xác nhận trên modal
   */
  const handleModalOk = (values: AdminFormParams) => {
    saveAdminMutation.mutate(values)
  }

  /**
   * Xử lý đóng modal
   */
  const handleModalCancel = () => {
    dispatch({
      modalVisible: false,
      currentRecord: null,
    })
  }

  const tableLoading = isFetching || saveAdminMutation.isPending

  return (
    <div className="flex h-full flex-col gap-2">
      <AdminSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          query: searchParams.query,
          is_active: searchParams.is_active,
          role_id: searchParams.role_id,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Danh sách Admin</h2>
            <Divider orientation="vertical" />
            <span className="text-gray-500 text-sm!">{`${state.selectedRowKeys.length} mục đã được chọn`}</span>
            <Divider orientation="vertical" />
            <AdminTableActions
              onAdd={handleAdd}
              onRefresh={refetch}
              onBatchDelete={() => alert("Không thể xoá!")}
              selectedRowKeys={state.selectedRowKeys}
              loading={tableLoading}
            />
          </div>
        }
      >
        <AdminTable
          data={result?.data || []}
          loading={tableLoading}
          selectedRowKeys={state.selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onEdit={handleEdit}
          onDelete={() => alert("Không thể xoá!")}
          onStatusChange={handleStatusChange}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <AdminModal
        open={state.modalVisible}
        title={state.modalTitle}
        loading={saveAdminMutation.isPending}
        initialValues={state.currentRecord}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      />
    </div>
  )
}
